const MD_QUERY = "(min-width: 768.1px)";
const AUTOPLAY_MS = 5000;
const FADE_MS = 100;

// Right-side fan arc (degrees, 0 = east / 3 o'clock, clockwise-positive in CSS,
// but SVG polar helper uses standard math: 0 east, positive counterclockwise)
const ARC_START = -78;
const ARC_END = 78;
const SEGMENT_GAP_DEG = 2.8;

const prefersReducedMotion = () =>
	window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const isDesktop = () => window.matchMedia(MD_QUERY).matches;

function polar(cx, cy, r, deg) {
	const rad = (deg * Math.PI) / 180;
	return {
		x: cx + r * Math.cos(rad),
		y: cy + r * Math.sin(rad),
	};
}

function annularSectorPath(cx, cy, rInner, rOuter, startDeg, endDeg) {
	const sweep = endDeg - startDeg;
	const large = Math.abs(sweep) > 180 ? 1 : 0;
	const outerStart = polar(cx, cy, rOuter, startDeg);
	const outerEnd = polar(cx, cy, rOuter, endDeg);
	const innerEnd = polar(cx, cy, rInner, endDeg);
	const innerStart = polar(cx, cy, rInner, startDeg);

	return [
		`M ${outerStart.x} ${outerStart.y}`,
		`A ${rOuter} ${rOuter} 0 ${large} 1 ${outerEnd.x} ${outerEnd.y}`,
		`L ${innerEnd.x} ${innerEnd.y}`,
		`A ${rInner} ${rInner} 0 ${large} 0 ${innerStart.x} ${innerStart.y}`,
		"Z",
	].join(" ");
}

function buildSegmentAngles(count) {
	const totalSpan = ARC_END - ARC_START;
	const gapTotal = SEGMENT_GAP_DEG * Math.max(count - 1, 0);
	const usable = totalSpan - gapTotal;
	const span = count > 0 ? usable / count : 0;

	const segments = [];
	let cursor = ARC_START;

	for (let i = 0; i < count; i += 1) {
		const start = cursor;
		const end = cursor + span;
		const mid = (start + end) / 2;
		segments.push({ start, end, mid, span });
		cursor = end + (i < count - 1 ? SEGMENT_GAP_DEG : 0);
	}

	return segments;
}

function setupRadial(root) {
	const stage = root.querySelector(".radial-stage");
	const copy = root.querySelector(".radial-copy");
	const titleEl = root.querySelector(".radial-title");
	const descEl = root.querySelector(".radial-desc");
	const orbit = root.querySelector(".radial-orbit");
	const svg = root.querySelector(".radial-ring-svg");
	const paths = Array.from(root.querySelectorAll(".radial-segment-path"));
	const items = Array.from(root.querySelectorAll(".radial-item"));

	if (!stage || !items.length) return;

	const data = items.map((item) => ({
		icon: item.querySelector("img")?.getAttribute("src") || "",
		title: item.dataset.title || "",
		content: item.dataset.content || "",
	}));

	let activeIndex = Math.max(
		0,
		items.findIndex((item) => item.classList.contains("is-active")),
	);
	let autoplayId = null;
	let fadeTimer = null;
	let isVisible = true;
	let angleMeta = buildSegmentAngles(items.length);

	function readMetrics() {
		const stageSize = stage.offsetWidth || 1;
		const styles = getComputedStyle(stage);
		const corePct = parseFloat(styles.getPropertyValue("--radial-core")) || 64;
		const thicknessPct =
			parseFloat(styles.getPropertyValue("--radial-ring-thickness")) || 13.5;
		const gapPx = parseFloat(styles.getPropertyValue("--radial-gap")) || 14;
		const cx = parseFloat(styles.getPropertyValue("--radial-cx")) || 50;
		const cy = parseFloat(styles.getPropertyValue("--radial-cy")) || 50;

		const gapPct = (gapPx / stageSize) * 100;
		const coreR = corePct / 2;
		const rInner = coreR + gapPct;
		const rOuterBase = rInner + thicknessPct;

		return { stageSize, cx, cy, rInner, rOuterBase };
	}

	function layoutRing() {
		if (!isDesktop() || !svg) return;

		const { stageSize, cx, cy, rInner, rOuterBase } = readMetrics();
		angleMeta = buildSegmentAngles(items.length);

		const rOuter = rOuterBase;
		const rMid = (rInner + rOuter) / 2;
		const ringDepth = rOuter - rInner;
		const depthPx = (ringDepth / 100) * stageSize;

		paths.forEach((path, i) => {
			const meta = angleMeta[i];
			if (!meta) return;

			path.setAttribute(
				"d",
				annularSectorPath(cx, cy, rInner, rOuter, meta.start, meta.end),
			);
			path.classList.toggle("is-active", i === activeIndex);
		});

		// Place icons at mid-angle, mid-radius of each equal sector
		items.forEach((item, i) => {
			const meta = angleMeta[i];
			if (!meta) return;

			const pt = polar(cx, cy, rMid, meta.mid);
			const xPx = (pt.x / 100) * stageSize;
			const yPx = (pt.y / 100) * stageSize;

			item.style.setProperty("--x", `${xPx}px`);
			item.style.setProperty("--y", `${yPx}px`);

			const chord =
				2 *
				rMid *
				Math.sin(((meta.span / 2) * Math.PI) / 180);
			const chordPx = (chord / 100) * stageSize;
			const iconPx = Math.max(
				15,
				Math.min(depthPx * 0.42, chordPx * 0.3, 24),
			);
			item.style.setProperty("--icon-size", `${iconPx}px`);
		});
	}

	function syncMobileScroll(index, instant = false) {
		if (isDesktop() || !orbit) return;

		const target = items[index];
		if (!target) return;

		const left =
			target.offsetLeft - (orbit.clientWidth - target.offsetWidth) / 2;

		orbit.scrollTo({
			left,
			behavior: instant || prefersReducedMotion() ? "auto" : "smooth",
		});
	}

	function render(index, { animate = true, fromUser = false } = {}) {
		const next = ((index % data.length) + data.length) % data.length;
		activeIndex = next;
		const itemData = data[next];

		items.forEach((item, i) => {
			const on = i === next;
			item.classList.toggle("is-active", on);
			const btn = item.querySelector(".radial-item-btn");
			if (btn) btn.setAttribute("aria-pressed", on ? "true" : "false");
		});

		paths.forEach((path, i) => {
			path.classList.toggle("is-active", i === next);
		});

		layoutRing();

		const shouldFade = animate && !prefersReducedMotion() && copy;
		const applyCopy = () => {
			if (titleEl) titleEl.textContent = itemData.title;
			if (descEl) descEl.textContent = itemData.content;
			copy?.classList.remove("is-fading");
		};

		if (shouldFade) {
			copy.classList.add("is-fading");
			clearTimeout(fadeTimer);
			fadeTimer = setTimeout(applyCopy, FADE_MS);
		} else {
			applyCopy();
		}

		if (fromUser) syncMobileScroll(next);
	}

	function stopAutoplay() {
		if (autoplayId) {
			clearInterval(autoplayId);
			autoplayId = null;
		}
	}

	function startAutoplay() {
		stopAutoplay();
		if (prefersReducedMotion() || !isVisible || document.hidden) return;

		autoplayId = setInterval(() => {
			render(activeIndex + 1);
			syncMobileScroll(activeIndex);
		}, AUTOPLAY_MS);
	}

	function restartAutoplay() {
		stopAutoplay();
		startAutoplay();
	}

	items.forEach((item, index) => {
		const btn = item.querySelector(".radial-item-btn");
		if (!btn) return;

		btn.addEventListener("mouseenter", () => {
			render(index, { fromUser: true });
			restartAutoplay();
		});
	});

	// SVG paths are decorative; hovers go through icon buttons.
	// Also allow hovering the path itself via pointer-events on active hit area.
	paths.forEach((path, index) => {
		path.addEventListener("mouseenter", () => {
			render(index, { fromUser: true });
			restartAutoplay();
		});
	});

	copy?.addEventListener("mouseenter", stopAutoplay);
	copy?.addEventListener("mouseleave", startAutoplay);

	if (orbit) {
		let scrollTick = false;
		orbit.addEventListener(
			"scroll",
			() => {
				if (isDesktop() || scrollTick) return;
				scrollTick = true;
				requestAnimationFrame(() => {
					scrollTick = false;
					const orbitBox = orbit.getBoundingClientRect();
					const center = orbitBox.left + orbitBox.width / 2;
					let closest = activeIndex;
					let minDist = Infinity;

					items.forEach((item, index) => {
						const box = item.getBoundingClientRect();
						const dist = Math.abs(box.left + box.width / 2 - center);
						if (dist < minDist) {
							minDist = dist;
							closest = index;
						}
					});

					if (closest !== activeIndex) {
						render(closest, { animate: true });
					}
				});
			},
			{ passive: true },
		);
	}

	const media = window.matchMedia(MD_QUERY);
	const onBreakpoint = () => {
		layoutRing();
		render(activeIndex, { animate: false });
		syncMobileScroll(activeIndex, true);
	};
	if (media.addEventListener) media.addEventListener("change", onBreakpoint);
	else media.addListener(onBreakpoint);

	const resizeObserver =
		typeof ResizeObserver !== "undefined"
			? new ResizeObserver(() => layoutRing())
			: null;
	resizeObserver?.observe(stage);

	const visObserver =
		typeof IntersectionObserver !== "undefined"
			? new IntersectionObserver(
				(entries) => {
					isVisible = entries.some((entry) => entry.isIntersecting);
					if (isVisible) startAutoplay();
					else stopAutoplay();
				},
				{ threshold: 0.25 },
			)
			: null;
	visObserver?.observe(root);

	document.addEventListener("visibilitychange", () => {
		if (document.hidden) stopAutoplay();
		else startAutoplay();
	});

	layoutRing();
	render(activeIndex, { animate: false });
	startAutoplay();
}

export function initRadialFeature() {
	document.querySelectorAll("[data-radial-feature]").forEach(setupRadial);
}
